import { useTranslation } from "@/i18n";
import { Check } from "lucide-react";

/**
 * Time savings data from the import response
 */
interface TimeSavings {
  totalModelingTimeMinutes: number;
  totalReadingTimeMinutes: number;
  totalWorkingDaysSaved: number;
}

/**
 * Props for the CompletedStep component.
 */
interface CompletedStepProps {
  /** Optional success notice message */
  notice?: string | null;
  /** Callback to restart the workflow */
  onRestart: () => void;
  /** Callback to return to document review */
  onBackToReview: () => void;
  /** Time savings data from successful import */
  timeSavings?: TimeSavings | null;
}

/**
 * Completion step component shown after successful process import.
 * Displays success message and offers three actions:
 * 1. Upload a new document (restart workflow)
 * 2. View imported processes in aiio
 * 3. Return to document review
 *
 * @param props - Component props
 * @returns Completion UI with action buttons
 */
export default function CompletedStep({
  notice,
  onRestart,
  onBackToReview,
  timeSavings,
}: CompletedStepProps) {
  const { t } = useTranslation();

  /**
   * Format minutes to hours display (e.g., "4h" or "431h")
   */
  const formatMinutesToHours = (minutes: number): string => {
    const hours = Math.round(minutes / 60);
    return `${hours}h`;
  };

  /**
   * Format working days to display (e.g., "36" or "2.95" → "3")
   */
  const formatWorkingDays = (days: number): string => {
    return Math.round(days).toString();
  };

  return (
    <div className="flex flex-col items-center justify-center gap-[60px] p-8 h-full mx-auto my-48">
      <div className="flex items-center gap-2.5 justify-center w-full">
        <div className="w-8 h-8 rounded-full bg-[#8e2bf0] flex items-center justify-center">
          <Check className="w-4 h-4 text-white" strokeWidth={1.33} />
        </div>
        <h2 className="text-[20px] font-semibold text-[#3f3f46] leading-normal">
          {t("documentUpload.completed.title")}
        </h2>
      </div>

      {timeSavings && (
        <div className="flex flex-col gap-5 items-center w-full">
          <div className="flex items-center justify-center w-full">
            <p className="text-[16px] font-semibold text-[#3f3f46] text-center">
              {t("documentUpload.completed.timeSavingsLabel")}
            </p>
          </div>
          <div className="flex gap-6 items-start">
            {/* Process Modelling Card */}
            <div className="bg-white rounded-xl shadow-[0px_2px_4px_0px_rgba(0,0,0,0.14),0px_0px_2px_0px_rgba(0,0,0,0.12)] px-5 py-4 flex flex-col gap-2.5 items-center">
              <div className="w-[124px] h-[124px] rounded-full opacity-90 relative overflow-hidden">
                <img
                  src="/modelling.svg"
                  alt="Process Modelling"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex gap-2.5 items-center justify-center w-full min-h-[40px]">
                <p className="text-[14px] font-semibold text-[#3f3f46] whitespace-pre-wrap flex-1 min-w-[50px]">
                  {t("documentUpload.completed.modeling")}
                </p>
                <p className="text-[31px] font-semibold text-[#3f3f46] text-center">
                  {formatMinutesToHours(timeSavings.totalModelingTimeMinutes)}
                </p>
              </div>
            </div>

            {/* Reading Time Card */}
            <div className="bg-white rounded-xl shadow-[0px_2px_4px_0px_rgba(0,0,0,0.14),0px_0px_2px_0px_rgba(0,0,0,0.12)] px-5 py-4 flex flex-col gap-2.5 items-center">
              <div className="w-[124px] h-[124px] rounded-full opacity-90 relative overflow-hidden">
                <img
                  src="/reading.svg"
                  alt="Reading Time"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex gap-2.5 items-center justify-center w-full min-h-[40px]">
                <p className="text-[14px] font-semibold text-[#3f3f46] whitespace-pre-wrap flex-1 min-w-[50px]">
                  {t("documentUpload.completed.readingTime")}
                </p>
                <p className="text-[19px] font-semibold text-[#3f3f46] text-center flex-1 whitespace-nowrap">
                  ~{formatMinutesToHours(timeSavings.totalReadingTimeMinutes)}
                </p>
              </div>
            </div>

            {/* Working Days Card */}
            <div className="bg-white rounded-xl shadow-[0px_2px_4px_0px_rgba(0,0,0,0.14),0px_0px_2px_0px_rgba(0,0,0,0.12)] px-5 py-4 flex flex-col gap-2.5 items-center">
              <div className="w-[124px] h-[124px] rounded-full opacity-90 relative overflow-hidden">
                <img
                  src="/working.svg"
                  alt="Working Days"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex gap-2.5 items-center justify-center w-full min-h-[40px]">
                <p className="text-[14px] font-semibold text-[#3f3f46] whitespace-pre-wrap flex-1 min-w-[50px]">
                  {t("documentUpload.completed.workDays")}
                </p>
                <p className="text-[31px] font-semibold text-[#3f3f46] text-center">
                  {formatWorkingDays(timeSavings.totalWorkingDaysSaved)}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {notice && (
        <p className="text-sm text-[#71717a] max-w-md text-center">{notice}</p>
      )}

      <div className="flex gap-4 items-center">
        <button
          onClick={onBackToReview}
          className="flex items-center justify-center gap-2 px-3 py-1.5 bg-white border border-[#e4e4e7] rounded-lg text-[#18181b] text-sm hover:bg-[#f4f4f5] transition-colors cursor-pointer"
        >
          <span className="px-0.5 pb-0.5">
            {t("common.buttons.backToReview")}
          </span>
        </button>
        <button
          onClick={onRestart}
          className="flex items-center justify-center gap-2 px-3 py-1.5 bg-white border border-[#e4e4e7] rounded-lg text-[#18181b] text-sm hover:bg-[#f4f4f5] transition-colors cursor-pointer"
        >
          <span className="px-0.5 pb-0.5">
            {t("common.buttons.newDocument")}
          </span>
        </button>
        <button
          onClick={() => {
            let aiioUrl = "https://app.aiio.de";
            const suffix = ".forge.aiio.de";
            const hostname = window.location.hostname;

            if (hostname.endsWith(suffix)) {
              const subdomain = hostname.substring(
                0,
                hostname.length - suffix.length
              );
              if (subdomain) {
                aiioUrl = `https://${subdomain}.aiio.de`;
              }
            }

            window.open(`${aiioUrl}/process-management/processes`, "_blank");
          }}
          className="flex items-center justify-center gap-2 px-3 py-1.5 bg-[#8e2bf0] border border-[#8e2bf0] rounded-lg text-white text-sm hover:bg-[#8e2bf0] transition-colors cursor-pointer"
        >
          <span className="px-0.5 pb-0.5">
            {t("common.buttons.showProcesses")}
          </span>
        </button>
      </div>
    </div>
  );
}
